'use client'

import React from 'react'
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis } from 'recharts'
import { ChartTooltip } from './ChartTooltip'

function humanizeSeriesKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/^./, (c) => c.toUpperCase())
}

export type SparkSeries = {
  key: string
  color: string
  dashed?: boolean
}

export type SparkSubStat = {
  label: string
  value: number
  color: string
}

export function SparkCard({
  id,
  label,
  value,
  sub1,
  sub2,
  trend,
  chartData,
  series,
  isDark = true,
  className = 'h-28',
  valueSuffix,
}: {
  id: string
  label: string
  value: number
  sub1?: SparkSubStat
  sub2?: SparkSubStat
  trend?: string
  chartData: Record<string, any>[]
  series: SparkSeries[]
  isDark?: boolean
  className?: string
  valueSuffix?: string
}) {
  const cardBg  = 'bg-card'
  const border  = 'border-border'
  const muted   = 'text-muted-foreground'
  const text    = 'text-foreground'

  const trendPositive = trend?.startsWith('+')
  const trendColor    = trendPositive ? 'text-success' : 'text-muted-foreground'
  const chartSignature = React.useMemo(
    () => chartData.map((d) => [d.date, ...series.map((s) => d[s.key] ?? '')].join(':')).join('|'),
    [chartData, series],
  )

  return (
    <div className={`relative flex flex-col overflow-hidden border ${border} ${cardBg} ${className}`}>
      {/* header row */}
      <div className="flex items-start justify-between px-3 pt-2">
        <div className={`font-mono text-[9px] uppercase tracking-widest ${muted}`}>{label}</div>
        {trend && (
          <div className={`font-mono text-[9px] font-semibold tabular-nums ${trendColor}`}>{trend}</div>
        )}
      </div>

      {/* big number */}
      <div className={`px-3 mt-1 font-mono text-2xl font-semibold tabular-nums leading-none ${text}`}>
        {value}{valueSuffix && <span className="ml-1 text-sm font-medium">{valueSuffix}</span>}
      </div>

      {/* sub-stats */}
      {(sub1 || sub2) && (
        <div className="px-3 mt-2 flex gap-4">
          {[sub1, sub2].filter(Boolean).map((s) => (
            <div key={s!.label} className="flex items-baseline gap-1">
              <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: s!.color + '99' }}>
                {s!.label}
              </span>
              <span className="font-mono text-[11px] font-medium tabular-nums" style={{ color: s!.color }}>
                {s!.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* sparkline — bleeds to bottom */}
      <div className="mt-auto h-10">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart key={chartSignature} data={chartData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
            <XAxis dataKey="date" hide />
            <Tooltip
              cursor={{ stroke: 'var(--subtle-text)', strokeWidth: 1, strokeDasharray: '3 3' }}
              position={{ y: -76 }}
              wrapperStyle={{ pointerEvents: 'none', zIndex: 20 }}
              content={({ active, payload, label }: any) => (
                <ChartTooltip
                  active={active}
                  payload={payload}
                  label={label}
                  isDark={isDark}
                  labelMap={Object.fromEntries(series.map((s) => [s.key, humanizeSeriesKey(s.key)]))}
                />
              )}
            />
            <defs>
              {series.map(({ key, color }) => (
                <linearGradient key={key} id={`${id}-${key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={color} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={color} stopOpacity={0.04} />
                </linearGradient>
              ))}
            </defs>
            {series.map(({ key, color, dashed }) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stroke={color}
                strokeWidth={1.5}
                strokeDasharray={dashed ? '4 2' : undefined}
                fill={`url(#${id}-${key})`}
                dot={false}
                isAnimationActive={false}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
